import { Component } from '@theme/component';

const MAX_SUGGESTIONS = 6;

/**
 * @typedef {{ id: string, question: string, category: string }} FaqEntry
 *
 * @typedef {object} FaqSearchRefs
 * @property {HTMLInputElement} searchInput
 * @property {HTMLUListElement} suggestionsList
 *
 * @extends {Component<FaqSearchRefs>}
 */
export class FaqSearchComponent extends Component {
  requiredRefs = ['searchInput', 'suggestionsList'];

  /** @type {FaqEntry[]} */
  #faqs = [];
  /** @type {FaqEntry[]} */
  #activeSuggestions = [];
  #activeSuggestionIndex = -1;

  connectedCallback() {
    super.connectedCallback();
    this.#loadFaqs();
  }

  /**
   * FAQ search data ships inline (a <script type="application/json"> tag
   * rendered by sections/faq-metaobject.liquid from the same sorted_faqs
   * used for the accordion), not fetched — the FAQ set is small enough
   * (well under Liquid's ~50-item default cap) that no static-asset or
   * pagination workaround is needed here, unlike the dealer map.
   */
  #loadFaqs() {
    const id = this.dataset.suggestionsId;
    const script = id ? document.getElementById(id) : null;
    if (!(script instanceof HTMLScriptElement)) return;

    try {
      const parsed = JSON.parse(script.textContent ?? '[]');
      this.#faqs = Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('[faq-search] failed to parse FAQ search data:', error);
    }
  }

  /** @param {Event} event */
  handleSearchInput(event) {
    const query = /** @type {HTMLInputElement} */ (event.target).value.trim().toLowerCase();

    if (!query) {
      this.#renderSuggestions([]);
      return;
    }

    const matches = this.#faqs.filter((faq) => faq.question.toLowerCase().includes(query)).slice(0, MAX_SUGGESTIONS);

    this.#renderSuggestions(matches);
  }

  /** @param {KeyboardEvent} event */
  handleSearchKeydown(event) {
    if (!this.#activeSuggestions.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.#moveSuggestionSelection(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.#moveSuggestionSelection(-1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const faq = this.#activeSuggestions[this.#activeSuggestionIndex] ?? this.#activeSuggestions[0];
      if (faq) this.#selectFaq(faq);
    } else if (event.key === 'Escape') {
      this.#renderSuggestions([]);
    }
  }

  /** @param {Event} event */
  handleSuggestionClick(event) {
    // See assets/dealer-map.js: the theme's on:pointerdown dispatch proxies
    // event.target to the resolved element but leaves currentTarget as the
    // delegated document-level listener — target is what we want here.
    const target = /** @type {HTMLElement} */ (event.target);
    const index = Number(target.dataset.suggestionIndex);
    const faq = this.#activeSuggestions[index];
    if (faq) this.#selectFaq(faq);
  }

  /** @param {FaqEntry[]} faqs */
  #renderSuggestions(faqs) {
    this.#activeSuggestions = faqs;
    this.#activeSuggestionIndex = -1;

    const list = this.refs.suggestionsList;
    list.textContent = '';
    this.refs.searchInput.setAttribute('aria-expanded', faqs.length > 0 ? 'true' : 'false');
    this.refs.searchInput.setAttribute('aria-activedescendant', '');

    if (!faqs.length) {
      list.hidden = true;
      return;
    }

    faqs.forEach((faq, index) => {
      const item = document.createElement('li');
      item.id = `faq-metaobject-suggestion-${index}`;
      item.className = 'faq-metaobject__suggestion';
      item.dataset.suggestionIndex = String(index);
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');
      item.setAttribute('on:pointerdown', '/handleSuggestionClick');

      const question = document.createElement('span');
      question.className = 'faq-metaobject__suggestion-question';
      question.textContent = faq.question;

      const category = document.createElement('span');
      category.className = 'faq-metaobject__suggestion-category';
      category.textContent = faq.category;

      item.append(question, category);
      list.append(item);
    });

    list.hidden = false;
  }

  /** @param {number} delta */
  #moveSuggestionSelection(delta) {
    const count = this.#activeSuggestions.length;

    if (this.#activeSuggestionIndex === -1) {
      this.#activeSuggestionIndex = delta > 0 ? 0 : count - 1;
    } else {
      this.#activeSuggestionIndex = (this.#activeSuggestionIndex + delta + count) % count;
    }

    const items = [...this.refs.suggestionsList.children];
    items.forEach((item, index) => {
      item.setAttribute('aria-selected', String(index === this.#activeSuggestionIndex));
    });

    const activeItem = items[this.#activeSuggestionIndex];
    this.refs.searchInput.setAttribute('aria-activedescendant', activeItem?.id ?? '');
  }

  /** @param {FaqEntry} faq */
  #selectFaq(faq) {
    const details = document.getElementById(faq.id);
    if (details instanceof HTMLDetailsElement) {
      details.open = true;
      details.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    this.#renderSuggestions([]);
  }
}

customElements.define('faq-search-component', FaqSearchComponent);
